import { useId, useRef, type JSX, type ReactNode } from 'react';

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command.js';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover.js';
import { useMediaQuery } from '@/hooks/useMediaQuery.js';
import { type Skill } from '@/lib/api/skills.js';

import {
  COMPOSER_POPOVER_LIST_HEIGHT,
  useScrollHighlightIntoView,
} from './composer-popover-list.js';
import {
  findMatchRange,
  type SlashMenuSection,
  type SlashSectionId,
} from './slash-skill-match.js';

interface SlashCommandMenuProps {
  /** Mirrors `useComposer`'s `slashState !== null && slashState.open`. */
  open: boolean;
  /** What the menu lists, already filtered, deduped and ordered — `useSlashSkillPicker`'s
   *  `slashSections`, the same list Enter and Tab pick from. */
  sections: SlashMenuSection<Skill>[];
  /** `slashState.query` — the trigger text typed so far, after the `/`; its match is highlighted. */
  query: string;
  /** Index into the flattened `sections` of the highlighted skill — `useComposer`'s
   *  `slashHighlightIndex`, moved by ArrowUp/ArrowDown in the composer (the textarea, not this
   *  popup, owns keyboard focus). */
  highlightIndex: number;
  /** Fired when the highlight should move — keyboard nav updates it from the composer side; cmdk
   *  itself calls this on mouse hover over an item (its built-in controlled-`value` behavior). */
  onHighlightChange: (index: number) => void;
  onSelect: (skill: Skill) => void;
  /** Fired when the popover wants to close (outside click / focus loss) — wired to the
   *  composer's Escape-style dismissal so the menu can't get stuck open. */
  onDismiss: () => void;
  /** The composer textarea — the popup anchors to it via `PopoverAnchor asChild`. */
  children: ReactNode;
}

/** Header over each bare-`/` section; a typed query's ranked `matches` list has none. */
const SECTION_HEADINGS: Record<SlashSectionId, string | undefined> = {
  pinned: 'Pinned',
  recent: 'Recent',
  all: 'All skills',
  matches: undefined,
};

/** Room for the description block beside the list, and a mouse to move the highlight with. */
const FLOATING_DETAILS_MEDIA = '(min-width: 768px) and (pointer: fine)';

/** The badge text for a skill's scope; `corporate` and any unknown scope read as `Company`. */
function scopeLabel(scope: string): string {
  if (scope === 'private') return 'Private';
  if (scope === 'workspace') return 'Workspace';
  return 'Company';
}

/** The small outlined scope label: in the details block on wide screens, in the row otherwise. */
function ScopeBadge({ scope }: { scope: string }): JSX.Element {
  return (
    <span className="ml-auto flex-none rounded border border-border px-1 text-[10px] leading-4 text-muted-foreground">
      {scopeLabel(scope)}
    </span>
  );
}

/** `text` with the first occurrence of `query` (as typed or in latin layout) wrapped in `<mark>`. */
function HighlightedText({
  text,
  query,
}: {
  text: string;
  query: string;
}): JSX.Element {
  const range = findMatchRange(text, query);
  if (!range) return <>{text}</>;
  const [start, end] = range;
  return (
    <>
      {text.slice(0, start)}
      <mark className="rounded-sm bg-amber-500/30 text-inherit">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

/**
 * Slash-command popup: a cmdk `Command` list rendered inside a Radix
 * `Popover` anchored to the composer textarea (passed as `children`).
 * Entirely driven by `useComposer`'s `slashState` and `slashSections` — there
 * is no `CommandInput` because the textarea itself is the text source, and no
 * filtering here at all (`shouldFilter={false}`): the picker hook builds the
 * list once, so the highlighted row and the row Enter picks can never differ.
 * There is no empty state either — the hook closes the menu when nothing
 * matches, so a literal `/something` sends as typed.
 *
 * A bare `/` shows the Pinned, Recent and All skills sections under headers;
 * a typed query shows one ranked list without a header. Each row is the
 * `/trigger`, with the matched part highlighted. The title stays out of the
 * row. The row's accessible name is spelled out (`/trigger, Scope`) so the
 * highlight `<mark>` never splits the trigger when read aloud.
 *
 * On a wide screen with a fine pointer, the highlighted skill's title, scope
 * badge (`Private` / `Workspace` / `Company`) and full description (with a
 * description match highlighted) sit in a
 * block beside the list, inside the same `PopoverContent`, so no second
 * popover has to track the anchor. On a narrow screen or a touch device
 * (Teams tabs) there is no hover and no room for it, so each row instead
 * carries the scope badge and its description clamped to two lines under the
 * trigger.
 *
 * Keyboard focus never leaves the composer textarea, so cmdk's own
 * keydown-driven navigation can't reach this list — `Command`'s `value` /
 * `onValueChange` are used in their *controlled* mode instead: `value` mirrors
 * `highlightIndex` (rendering cmdk's normal `data-selected` highlight style on
 * that item) and `onValueChange` reports back both a keyboard-driven move
 * (from `useComposer.onKeyDown`, which triggers a `highlightIndex` prop
 * change) and a mouse hover over a different item (cmdk fires this on its
 * own). Enter is still handled by `useComposer.onKeyDown` directly, picking
 * `slashMatches[highlightIndex]` — see `selectSlashSkill`.
 */
export function SlashCommandMenu({
  open,
  sections,
  query,
  highlightIndex,
  onHighlightChange,
  onSelect,
  onDismiss,
  children,
}: SlashCommandMenuProps): JSX.Element {
  const matches = sections.flatMap((section) => section.skills);
  const highlightedSkill = matches[highlightIndex];
  const highlightedSkillId = highlightedSkill?.id ?? '';
  const showsFloatingDetails = useMediaQuery(FLOATING_DETAILS_MEDIA);
  const floatingSkill =
    showsFloatingDetails &&
    highlightedSkill &&
    (highlightedSkill.title || highlightedSkill.description)
      ? highlightedSkill
      : null;

  const descriptionIdPrefix = useId();
  const floatingDescriptionId = `${descriptionIdPrefix}-floating`;
  const inlineDescriptionId = (skillId: string): string =>
    `${descriptionIdPrefix}-inline-${skillId}`;
  const floatingHasDescription = Boolean(floatingSkill?.description);

  const listRef = useRef<HTMLDivElement>(null);
  useScrollHighlightIntoView(listRef, open, highlightedSkillId);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDismiss();
      }}
    >
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        collisionPadding={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="flex w-auto items-start gap-2 border-0 bg-transparent p-0 shadow-none"
      >
        <div className="w-80 rounded-lg border border-border bg-popover p-1 shadow-elev-4">
          <Command
            shouldFilter={false}
            value={highlightedSkillId}
            onValueChange={(value) => {
              const index = matches.findIndex((skill) => skill.id === value);
              if (index >= 0) onHighlightChange(index);
            }}
          >
            <CommandList ref={listRef} className={COMPOSER_POPOVER_LIST_HEIGHT}>
              {sections.map((section) => (
                <CommandGroup
                  key={section.id}
                  heading={SECTION_HEADINGS[section.id]}
                  className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground"
                >
                  {section.skills.map((skill) => (
                    <CommandItem
                      key={skill.id}
                      value={skill.id}
                      aria-label={`/${skill.trigger}, ${scopeLabel(skill.scope)}`}
                      aria-describedby={
                        !skill.description
                          ? undefined
                          : showsFloatingDetails
                            ? floatingHasDescription &&
                              skill.id === floatingSkill?.id
                              ? floatingDescriptionId
                              : undefined
                            : inlineDescriptionId(skill.id)
                      }
                      onSelect={() => onSelect(skill)}
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate font-mono text-[13px]">
                            /
                            <HighlightedText
                              text={skill.trigger}
                              query={query}
                            />
                          </span>
                          {showsFloatingDetails ? null : (
                            <ScopeBadge scope={skill.scope} />
                          )}
                        </div>
                        {!showsFloatingDetails && skill.description ? (
                          <span
                            id={inlineDescriptionId(skill.id)}
                            className="line-clamp-2 text-xs text-muted-foreground"
                          >
                            {skill.description}
                          </span>
                        ) : null}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </div>
        {floatingSkill ? (
          <aside
            aria-label="Skill details"
            className={`w-72 overflow-y-auto rounded-lg border border-border bg-popover p-3 text-sm text-popover-foreground shadow-elev-4 ${COMPOSER_POPOVER_LIST_HEIGHT}`}
          >
            <div className="mb-2 flex items-start gap-2">
              <h4 className="min-w-0 flex-1 font-medium">
                {floatingSkill.title}
              </h4>
              <ScopeBadge scope={floatingSkill.scope} />
            </div>
            {floatingSkill.description ? (
              <p id={floatingDescriptionId} className="whitespace-pre-line">
                <HighlightedText
                  text={floatingSkill.description}
                  query={query}
                />
              </p>
            ) : null}
          </aside>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
